/**
 * CoefficientHistoryPage — per-org changelog of every mutation that
 * touched this tenant's resolved ROI coefficient bundle in the last
 * 30 days (industry-library edits + org-level overrides).
 *
 * The forensic trail behind the header "Coefficients edited N days
 * ago" pill — answers "why did our projected ROI move 8 points this
 * quarter?" with a chronological list grouped by date.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';
import { toast } from 'sonner';
import {
  History, Loader2, Pencil, Archive, ArchiveRestore, Trash2,
  Layers, Building2, RefreshCw, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/layout/PageShell';
import PageHero from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  updated:               { icon: Pencil,         label: 'Edited',     scope: 'Industry',     classes: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  archived:              { icon: Archive,        label: 'Archived',   scope: 'Industry',     classes: 'bg-amber-50 border-amber-200 text-amber-700' },
  restored:              { icon: ArchiveRestore, label: 'Restored',   scope: 'Industry',     classes: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  hard_deleted:          { icon: Trash2,         label: 'Deleted',    scope: 'Industry',     classes: 'bg-rose-50 border-rose-200 text-rose-700' },
  org_override_updated:  { icon: Building2,      label: 'Org tweak',  scope: 'Your org',     classes: 'bg-sky-50 border-sky-200 text-sky-700' },
  org_override_cleared:  { icon: Building2,      label: 'Org reset',  scope: 'Your org',     classes: 'bg-slate-50 border-slate-200 text-slate-600' },
};

function relativeStamp(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function dayBucket(iso) {
  if (!iso) return 'Earlier';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yesterday = new Date(today.getTime() - 86400000);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  // Within the last week: weekday name; older: full date.
  const ageDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (ageDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: ageDays > 180 ? 'numeric' : undefined });
}

export default function CoefficientHistoryPage() {
  const { user, viewAsOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = viewAsOrg?.id || user?.organization_id;
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ org_name: null, industry_id: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pageTheme = useMemo(() => getLightSectionTheme('Indigo'), []);

  const load = useCallback(async (window) => {
    if (!orgId) return;
    setRefreshing(true);
    try {
      const res = await authedFetch(`${API}/api/organizations/${orgId}/roi-profile/changelog?days=${window}&limit=200`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRows(data?.changelog || []);
      setMeta({ org_name: data?.org_name, industry_id: data?.industry_id });
    } catch (e) {
      toast.error(`Failed to load changelog: ${e.message || e}`);
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orgId]);

  useEffect(() => { load(days); }, [load, days]);

  // Group consecutive rows by their day bucket so the timeline reads
  // naturally as "Today · Yesterday · Tuesday · …" headers.
  const buckets = useMemo(() => {
    const out = [];
    let currentLabel = null;
    let currentRows = [];
    for (const r of rows) {
      const label = dayBucket(r.created_at);
      if (label !== currentLabel) {
        if (currentRows.length) out.push({ label: currentLabel, rows: currentRows });
        currentLabel = label;
        currentRows = [];
      }
      currentRows.push(r);
    }
    if (currentRows.length) out.push({ label: currentLabel, rows: currentRows });
    return out;
  }, [rows]);

  const heroStats = useMemo(() => {
    const industryEdits = rows.filter((r) => (r.source || 'library') === 'library').length;
    const orgEdits = rows.filter((r) => r.source === 'org_override').length;
    const actors = new Set(rows.map((r) => r.actor_email).filter(Boolean));
    return [
      { label: 'Changes', value: String(rows.length) },
      { label: 'Industry edits', value: String(industryEdits) },
      { label: 'Org tweaks', value: String(orgEdits) },
      { label: 'Distinct editors', value: String(actors.size) },
    ];
  }, [rows]);

  if (!orgId) {
    return (
      <PageShell theme={pageTheme} hero={(
        <PageHero
          icon={History}
          theme={pageTheme}
          title="Coefficient History"
          description="No organization context — pick an org first."
          testId="coefficient-history-hero"
        />
      )}>
        <div className="px-4 md:px-8 lg:px-12 py-12 text-sm text-slate-500" data-testid="coefficient-history-no-org">
          No active organization. Use the Switch Org dropdown in the header.
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      theme={pageTheme}
      hero={(
        <PageHero
          icon={History}
          theme={pageTheme}
          title="Coefficient History"
          description={
            meta.industry_id
              ? `Every change in the last ${days} days that touched ${meta.org_name || 'this org'}'s resolved ROI profile — industry ${meta.industry_id} mutations + your org-level overrides, oldest to newest.`
              : `Every change in the last ${days} days that touched ${meta.org_name || 'this org'}'s ROI profile. Tag an industry on this org to see library-side mutations too.`
          }
          stats={heroStats}
          testId="coefficient-history-hero"
        />
      )}
      data-testid="coefficient-history-page"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 py-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            Filter window
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-mono"
              data-testid="coefficient-history-window"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(days)}
              disabled={refreshing}
              data-testid="coefficient-history-refresh"
            >
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading changelog…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="coefficient-history-empty">
            <History className="mx-auto mb-3 h-8 w-8 text-indigo-400" />
            <p className="text-sm font-medium text-slate-700">
              No coefficient changes in the last {days} days
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Your ROI numbers are running against the same baselines as the start of the window. Try widening the filter or check back after the next library update.
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="coefficient-history-timeline">
            {buckets.map((b, bi) => (
              <div key={b.label + bi}>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                  {b.label}
                </div>
                <ul className="space-y-2">
                  {b.rows.map((r) => {
                    const meta = ACTION_META[r.action] || ACTION_META.updated;
                    const Icon = meta.icon;
                    const isOrgScope = (r.source || 'library') === 'org_override';
                    return (
                      <li
                        key={r.id}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                        data-testid={`coefficient-history-row-${r.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`shrink-0 rounded-lg p-2 ${meta.classes}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${meta.classes}`}>
                                {meta.label}
                              </span>
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                                {isOrgScope ? <Building2 className="h-2.5 w-2.5" /> : <Layers className="h-2.5 w-2.5" />}
                                {meta.scope}
                              </span>
                              <span className="text-xs font-semibold text-slate-900 truncate">
                                {isOrgScope ? (meta.org_name || 'Org-level tweak') : (r.industry_name || r.industry_id)}
                              </span>
                              <span className="text-[10px] text-slate-400" title={r.created_at}>
                                {relativeStamp(r.created_at)}
                              </span>
                            </div>
                            {r.summary && (
                              <p className="text-xs text-slate-700 mt-1 leading-snug">{r.summary}</p>
                            )}
                            {r.actor_email && (
                              <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">
                                by {r.actor_email}
                              </p>
                            )}
                            {Array.isArray(r.diff) && r.diff.length > 0 && (
                              <details className="mt-2 group">
                                <summary className="text-[10px] text-indigo-600 cursor-pointer list-none flex items-center gap-1 hover:underline">
                                  <span>View diff ({r.diff.length} field{r.diff.length === 1 ? '' : 's'})</span>
                                </summary>
                                <ul className="mt-1.5 rounded-md border border-slate-200 bg-slate-50/40 p-2 text-[11px] space-y-1">
                                  {r.diff.map((d, i) => (
                                    <li key={i} className="flex items-baseline gap-2 font-mono">
                                      <span className="text-slate-500 shrink-0">{d.field}</span>
                                      <span className="text-rose-600 truncate">
                                        {d.kind === 'count' ? `${d.from} rows` : String(d.from ?? '∅')}
                                      </span>
                                      <span className="text-slate-400">→</span>
                                      <span className="text-emerald-700 truncate">
                                        {d.kind === 'count' ? `${d.to} rows` : String(d.to ?? '∅')}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            )}
                          </div>
                          {!isOrgScope && r.industry_id && (
                            <button
                              type="button"
                              onClick={() => navigate('/admin/industry-library')}
                              className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-800"
                              data-testid={`coefficient-history-jump-${r.id}`}
                              aria-label="Open Industry Library"
                            >
                              Library <ExternalLink className="h-2.5 w-2.5" />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
